import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials=true so the browser sends the httpOnly `eip_session` cookie AND
// receives the non-httpOnly `eip_csrf` cookie we echo back below.
const api = axios.create({ baseURL: API, withCredentials: true });

// Read the (non-httpOnly) csrf cookie so we can echo it back as X-CSRF-Token.
function readCookie(name) {
  const parts = (document.cookie || "").split(";");
  for (const p of parts) {
    const [k, ...rest] = p.trim().split("=");
    if (k === name) return decodeURIComponent(rest.join("="));
  }
  return null;
}

// Share-access token (for password-protected /view/ and /embed/ dashboards). Stored in
// memory here; the Viewer restores it from sessionStorage on mount.
let _shareAccessToken = null;
export function setShareAccessToken(tok) { _shareAccessToken = tok || null; }

// Grace period after a successful login/signup: during this window a lagging 401
// from a request that was fired BEFORE the login response reached the browser
// won't nuke the fresh session. The AuthContext calls markJustAuthed() after
// login() and signup() succeed.
let _justAuthedUntil = 0;
export function markJustAuthed(ms = 4000) { _justAuthedUntil = Date.now() + ms; }
export function inLoginGrace() { return Date.now() < _justAuthedUntil; }

// Attach CSRF header to every state-changing request. The backend skips validation
// for safe methods and for Authorization: Bearer callers.
api.interceptors.request.use((cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const tok = readCookie("eip_csrf");
    if (tok) {
      cfg.headers = cfg.headers || {};
      cfg.headers["X-CSRF-Token"] = tok;
    }
  }
  // If a share-access token is set (public/embed viewer verified password), echo it.
  if (_shareAccessToken) {
    cfg.headers = cfg.headers || {};
    cfg.headers["X-Share-Access"] = _shareAccessToken;
  }
  return cfg;
});

// Prime the CSRF cookie (via a cheap GET) then retry the original request. Rare —
// happens only when the browser drops the eip_csrf cookie mid-session (e.g. a
// user manually wiped site data) so the first DELETE/POST after that would 403.
async function primeCsrfAndRetry(cfg) {
  try {
    await axios.get(`${API}/health`, { withCredentials: true });
  } catch { /* even a 404 mints the cookie via the middleware */ }
  const tok = readCookie("eip_csrf");
  if (!tok) return null;
  const next = { ...cfg, _csrfRetried: true };
  next.headers = { ...(cfg.headers || {}), "X-CSRF-Token": tok };
  return api.request(next);
}

// Diagnostic banner trigger: the backend sets `X-Session-Reset` (via the stale-
// cookie sweeper) when it strips a bogus session. We surface this as a global
// event so a top-level listener can show a toast/banner explaining why the tab
// was suddenly logged out. We also persist the reason in sessionStorage so the
// toast survives the hard redirect to /login that typically follows.
function _emitSessionReset(res) {
  try {
    const hdr = res?.headers?.["x-session-reset"] || res?.headers?.["X-Session-Reset"];
    if (hdr) {
      try { sessionStorage.setItem("eip:pending-reset", String(hdr)); } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent("eip:session-reset", { detail: { reason: hdr } }));
    }
  } catch { /* headers not readable — CORS-safe fallback */ }
}

api.interceptors.response.use(
  (res) => { _emitSessionReset(res); return res; },
  async (err) => {
    _emitSessionReset(err?.response);
    if (err?.response?.status === 401) {
      const p = window.location.pathname;
      const cfg = err?.config || {};
      const url = String(cfg.url || "");
      // Login and refresh endpoints legitimately return 401 on bad credentials —
      // never redirect from those; let the caller handle the error.
      const isAuthCall = url.includes("/auth/login") || url.includes("/auth/signup")
        || url.includes("/auth/refresh") || url.includes("/auth/me")
        || url.includes("/auth/bootstrap");
      // Inside the login-grace window we retry the failing GET ONCE. By that
      // point the browser will have committed the Set-Cookie from the login
      // response, so the second attempt succeeds and the user isn't logged out
      // for a race they didn't cause.
      if (!isAuthCall && inLoginGrace() && !cfg._loginGraceRetried
          && String(cfg.method || "get").toLowerCase() === "get") {
        cfg._loginGraceRetried = true;
        await new Promise((r) => setTimeout(r, 350));
        return api.request(cfg);
      }
      if (!isAuthCall && !p.startsWith("/login") && !p.startsWith("/signup") && !p.startsWith("/forgot-password") && !p.startsWith("/reset-password") && !p.startsWith("/view/") && !p.startsWith("/embed/")) {
        window.location.href = "/login";
      }
    }
    // Auto-recover from a missing / stale CSRF cookie on state-changing requests.
    const cfg = err?.config || {};
    const detail = err?.response?.data?.detail || "";
    if (err?.response?.status === 403
        && typeof detail === "string" && detail.toLowerCase().includes("csrf")
        && !cfg._csrfRetried
        && ["post", "put", "patch", "delete"].includes(String(cfg.method || "").toLowerCase())) {
      const retried = await primeCsrfAndRetry(cfg);
      if (retried) return retried;
    }
    // Surface the explicit "Read-only impersonated session" 403 concisely.
    // We DON'T fire our own toast here — most callers already show
    // `toast.error(err?.response?.data?.detail || "…")`, so shortening the
    // detail lets the caller-side toast display the right thing without
    // stacking two toasts on top of each other.
    if (err?.response?.status === 403
        && typeof detail === "string"
        && detail.toLowerCase().includes("read-only impersonated")) {
      err.response.data.detail = "Read-only impersonation — end the session to make changes.";
    }
    return Promise.reject(err);
  }
);

export default api;
